import {Table as AntTable} from 'antd';
import {TablePaginationConfig} from 'antd/es/table';
import Column from 'antd/es/table/Column';
import React, {useEffect, useMemo, useState} from 'react';

import {ContentUnavailable} from 'components/content-unavailable';
import {FormEditModal} from 'components/form-edit-modal/form-edit-modal';
import {SimpleActionButton} from 'components/form/components';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {UIBlocker} from 'components/ui-blocker';
import {selectContextRawData} from 'modules/context/context-selector';
import {loadTableData} from 'modules/data';
import {resetData} from 'modules/data/data-actions';
import {selectTableEntityData} from 'modules/data/data-selectors';
import {EEntityType} from 'shared/constants/entities';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {getTablePaginationDefaultConfig} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {controlRatioActions} from 'store/slices/control-ratio-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {Search} from '../../@common/inputs/search';
import {Table} from '../../table';
import {
    TControlRatioSettingsRulesSection,
    ControlRatioSettingsSection,
    ControlRatiosSettingRuleSections,
    CRIndicatorsSettingsSection,
    CrRulesTableDataUrl,
} from './constants/control-ratio-settings-rules';
import {CrRulesTableEntrySelector} from './cr-rules-table-entry-selector/cr-rules-table-entry-selector';

import './control-ratio-settings-rules.less';

export interface IControlRatioSettingsRulesProps {
    entity: string | null;
    entityName: string;
    handleFormClose: () => void;
    referenceUrlWithoutSlash: string;
}

export const ControlRatioSettingsRules = ({
    entity,
    entityName,
    handleFormClose,
    referenceUrlWithoutSlash,
}: IControlRatioSettingsRulesProps) => {
    const dispatch = useAppDispatch();

    const [section, setSection] = useState<TControlRatioSettingsRulesSection>(ControlRatioSettingsSection);
    const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
    const [search, setSearch] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [paginationTotal, setPaginationTotal] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);

    const sections = {
        [ControlRatioSettingsSection]: ControlRatioSettingsSection,
        [CRIndicatorsSettingsSection]: CRIndicatorsSettingsSection
    };

    const crData = useAppSelector(selectTableEntityData(ControlRatioSettingsSection));
    const indicatorsData = useAppSelector(selectTableEntityData(CRIndicatorsSettingsSection));
    const contextRawData = useAppSelector(selectContextRawData);
    const {hasUserRoleFunction} = useUserRoleFunctions({
        sections: [entityName],
    });
    const isUnavailable = !hasUserRoleFunction(EUserRoleFunction.VIEW);

    const fetchData = (searchValue?: string | null, pagination?: any) => {
        dispatch(loadTableData(
            sections[section],
            {paramsToBeConverted: {search: searchValue}, params: pagination},
            sections[section],
        ));
    };

    useEffect(() => {
        fetchData();
    }, [contextRawData]);

    // save selected entry id for form
    useEffect(() => {
        if (!selectedEntryId) return;
        if (section === ControlRatioSettingsSection) {
            dispatch(
                controlRatioActions.setFormAdditionalParameters({settingRulesControlRatio: {id: selectedEntryId}}),
            );
        } else {
            dispatch(controlRatioActions.setFormAdditionalParameters({settingRulesObjectItem: {id: selectedEntryId}}));
        }
    }, [section, selectedEntryId]);

    const data = useMemo(() => {
        const rows = section === ControlRatioSettingsSection ? crData?.rows : indicatorsData?.rows;
        if (selectedEntryId) return rows?.filter(row => row.id === selectedEntryId);
        return rows;
    }, [crData, indicatorsData, selectedEntryId, section]);

    const handleEntrySelect = (entry: number | null) => {
        dispatch(resetData({
            entityName,
            entityType: EEntityType.TABLE,
        }));
        setSelectedEntryId(entry);
    };

    const handlePaginationChange = (page: number, size?: number | undefined) => {
        if (!selectedEntryId) {
            setCurrentPage(page);
            setPageSize(size || 10);
            fetchData(search, {
                paginationCurrent: page,
                paginationPageSize: size,
            });
        }
    };

    const pagination: TablePaginationConfig = useMemo(() => ({
        ...getTablePaginationDefaultConfig(),
        total: paginationTotal,
        onChange: handlePaginationChange,
        current: currentPage,
        pageSize,
    }), [section, paginationTotal, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
        setPageSize(10);
        setPaginationTotal(() => {
            switch (section) {
            case ControlRatioSettingsSection: {
                return crData?.params?.paginationTotal;
            }
            case CRIndicatorsSettingsSection: {
                return indicatorsData?.params?.paginationTotal;
            }
            default:
                return 0;
            }
        });
        handlePaginationChange(1, 10);
    }, [section, crData?.params?.paginationTotal, indicatorsData?.params?.paginationTotal]);

    // state cleanup on unmount
    useEffect(() => () => {
        dispatch(controlRatioActions.resetFormAdditionalParameters());
    }, []);

    return (
        <UIBlocker
            content={
                isUnavailable && <ContentUnavailable className="control-ratio-settings-rule__content-unavailable" />
            }
            opacity={0}
        >
            <div className="control-ratio-settings-rule">
                <div className="control-ratio-settings-rule__buttons">
                    <SimpleActionButton
                        icon="ArrowLeft"
                        type="primary"
                        title="Назад"
                        disabled={!selectedEntryId}
                        onClick={() => setSelectedEntryId(null)}
                    />
                    <div className="section-switcher">
                        <CustomSelect
                            value={section}
                            entries={ControlRatiosSettingRuleSections}
                            onChange={value => {
                                setSection(value);
                                setSelectedEntryId(null);
                            }}
                        />
                    </div>
                    <Search
                        setSearch={setSearch}
                        fetchCallback={fetchData}
                    />
                </div>
                <AntTable
                    rowKey="id"
                    scroll={{x: 'max-content'}}
                    dataSource={data}
                    pagination={selectedEntryId ? false : pagination}
                    loading={!data}
                >
                    <Column
                        key="code"
                        dataIndex="code"
                        title="Код"
                        width={320}
                    />
                    <Column
                        key="name"
                        dataIndex="name"
                        title="Наименование"
                        render={(value, record: Record<string, any>) => (
                            <CrRulesTableEntrySelector
                                value={value}
                                record={record}
                                selectTableEntry={handleEntrySelect}
                            />
                        )}
                    />
                </AntTable>
            </div>
            <div className="cr-rules-table-wrapper">
                {selectedEntryId && (
                    <Table
                        entityName={entityName}
                        url={`${CrRulesTableDataUrl[section]}/${selectedEntryId}`}
                    />
                )}
            </div>
            {entity && !Number.isNaN(parseInt(entity, 10)) && (
                <FormEditModal
                    entityName={entityName}
                    entityType={EEntityType.FORM}
                    onClose={handleFormClose}
                    referenceUrl={`${referenceUrlWithoutSlash}/${entity}`}
                />
            )}
        </UIBlocker>
    );
};
